/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import {
    InvoicePaidStatus,
    InvoiceSearchCriteria,
    InvoiceType,
    IQueryResultSettings,
    OrderInvoice,
    SalesOrdersDataActions
} from '@msdyn365-commerce/retail-proxy';
import { getCurrentAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';

import getCustomer, { GetCustomerInput } from '../get-customer';
import { QueryResultSettingsProxy } from '../utilities/QueryResultSettingsProxy';

const getDefaultSearchCriteria = (): InvoiceSearchCriteria => {
    return {
        InvoicePaidStatusValues: [InvoicePaidStatus.None]
    };
};

const INVOICE_SORT_COLUMN_NAME = 'InvoiceIds';

/**
 * Input class for get invoice data action.
 */
export class GetInvoicesInput implements IActionInput {
    public readonly searchCriteria: InvoiceSearchCriteria;

    public readonly queryResultSettings: IQueryResultSettings;

    public readonly isInitialB2bContactLoad: boolean = true;

    public readonly isDescending: boolean;

    constructor(
        searchCriteria: InvoiceSearchCriteria,
        queryResultSettings: IQueryResultSettings,
        isInitialB2bContactLoad: boolean = true,
        isDescending?: boolean
    ) {
        this.searchCriteria = searchCriteria;
        this.isInitialB2bContactLoad = isInitialB2bContactLoad;
        const invoiceIdSortColumn = queryResultSettings.Sorting?.Columns?.find(column => column.ColumnName === INVOICE_SORT_COLUMN_NAME);
        this.isDescending = isDescending ?? invoiceIdSortColumn?.IsDescending ?? true;
        // Clone rather than mutating the caller's queryResultSettings in place: the component reuses a single shared
        // instance across refresh/load-more/filter changes, so overwriting Sorting on it would be a side effect on
        // shared state.
        this.queryResultSettings = {
            ...queryResultSettings,
            Sorting: {
                Columns: [
                    {
                        ColumnName: INVOICE_SORT_COLUMN_NAME,
                        IsDescending: this.isDescending
                    }
                ]
            }
        };
    }

    public getCacheKey = () => `BusinessPartnerInvoices-${this.isDescending ? 'desc' : 'asc'}`;

    public getCacheObjectType = () => 'OrderInvoice';

    public dataCacheType = (): CacheType => 'none';
}

export const CreateGetInvoicesInput = (inputData: ICreateActionContext): IActionInput => {
    const { requestContext } = inputData;

    // Check if the user is authenticated.
    if (!requestContext.user.isAuthenticated) {
        throw new Error('User is not logged in.');
    }

    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;

    return new GetInvoicesInput(getDefaultSearchCriteria(), queryResultSettings);
};

export async function getInvoices(input: GetInvoicesInput, context: IActionContext): Promise<OrderInvoice[]> {
    if (!context?.requestContext?.user?.isAuthenticated) {
        throw new Error('Unable to get list of invoices. User is not authenticated.');
    }

    const { user } = context.requestContext;

    // Invoice list only supports sales order invoices; apply the restriction for every user type
    // (including B2B contacts) so we don't change today's behavior for non-contact users.
    input.searchCriteria.InvoiceTypeValues = [InvoiceType.SalesOrderInvoice];

    if (user?.isB2bContact && input.isInitialB2bContactLoad) {
        input.searchCriteria.CustomerAccountNumbers = [user?.customerAccountNumber || ''];
        input.searchCriteria.ContactPersonIds = [user?.contactPersonId || ''];
    } else if (!user?.isB2bContact) {
        const businessPartner = await getCurrentAsync({ callerContext: context });
        input.searchCriteria.BusinessPartnerId = businessPartner?.BusinessPartnerId;

        const customer = await getCustomer(new GetCustomerInput(context.requestContext.apiSettings), context);
        input.searchCriteria.CustomerId = customer.AccountNumber;
    }

    return SalesOrdersDataActions.getInvoicesAsync(
        { callerContext: context, queryResultSettings: input.queryResultSettings },
        input.searchCriteria
    );
}

export const getInvoicesDataAction = createObservableDataAction({
    id: 'get-invoices',
    action: <IAction<OrderInvoice[]>>getInvoices,
    input: CreateGetInvoicesInput
});

export default getInvoicesDataAction;
